import { useEffect, useMemo, useState } from "react"
import { RATING_OPTIONS, REMOVED_REVIEW_MESSAGE } from "@/constants/community"
import { usePhotosDialog } from "@/hooks/use-photos-dialog"
import { PlaceReview } from "@/types/place"
import { PublicUser } from "@/types/user"
import { useInView } from "react-intersection-observer"

import { relativeCanonicalUrl } from "@/lib/entity-utils"
import { Locale } from "@/lib/locale-hooks"
import {
  useGetCurrentUserReviewsQuery,
  useGetUserReviewsQuery,
} from "@/lib/storage/api/place-api"
import { useAppSelector } from "@/lib/storage/hooks"
import { selectUser } from "@/lib/storage/user-slice"
import { userDisplayShortName } from "@/lib/user-utils"
import { Link } from "@/components/ui/link"
import { Loading } from "@/components/ui/loading"
import { EntityIcon } from "./entity-icon"
import { PlaceHoverCard } from "./place-hover-card"
import { PlaceLabel } from "./place-label"
import { ReviewActionsMenu } from "./review-actions-menu"
import { ReviewHighlights } from "./review-highlights"
import { AriaButton } from "./ui/aria-button"
import { Photo } from "./ui/photo"

const REVIEWS_PER_PAGE = 10

interface ProfileReviewsListProps {
  mode: "current" | "public"
  user?: PublicUser
}

export function ProfileReviewsList({ mode, user }: ProfileReviewsListProps) {
  const formatElapsedTimeOrDate = Locale.useFormatElapsedTimeOrDate()
  const currentUser = useAppSelector(selectUser)

  const { openPhotosDialog, PhotosDialog } = usePhotosDialog()

  const [page, setPage] = useState(1)
  const [reviews, setReviews] = useState<PlaceReview[]>([])
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: "50%",
  })

  const currentUserQuery = useGetCurrentUserReviewsQuery(
    { page, perPage: REVIEWS_PER_PAGE },
    { skip: mode !== "current" }
  )

  const publicUserQuery = useGetUserReviewsQuery(
    { handle: user?.handle || "", page, perPage: REVIEWS_PER_PAGE },
    { skip: mode !== "public" || !user?.handle }
  )

  const activeQuery = mode === "current" ? currentUserQuery : publicUserQuery

  const queryData = activeQuery.data
  const isLoading = activeQuery.isLoading || activeQuery.isFetching
  const totalPages = queryData?.meta.total_pages ?? 1
  const hasMore = page < totalPages

  useEffect(() => {
    setPage(1)
    setReviews([])
  }, [mode, user?.handle])

  useEffect(() => {
    if (!queryData) return
    if (page === 1) {
      setReviews(queryData.data)
      return
    }

    setReviews((prev) => {
      const existingIds = new Set(prev.map((review) => review.id))
      const nextItems = queryData.data.filter(
        (review) => !existingIds.has(review.id)
      )
      return [...prev, ...nextItems]
    })
  }, [queryData, page])

  useEffect(() => {
    if (!inView || isLoading || !hasMore) return
    setPage((prev) => prev + 1)
  }, [hasMore, inView, isLoading])

  const rendered = useMemo(
    () => reviews.filter((review) => !!review.place),
    [reviews]
  )

  if (rendered.length === 0 && isLoading) {
    return <Loading className="m-auto" />
  }

  if (rendered.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <span className="mb-5 flex size-20 items-center justify-center font-emoji text-4xl">
          👀
        </span>
        <h2 className="mb-2 text-md font-semibold sm:text-lg">
          <span>{userDisplayShortName(user ?? currentUser)}</span> hasn’t
          written any reviews yet.
        </h2>
        <p className="sm:text-md">Check back in later.</p>
      </div>
    )
  }

  return (
    <>
      <div className="mx-auto flex w-full max-w-lg flex-col gap-8 sm:pt-3 lg:pt-7">
        {rendered.map((review) => {
          const place = review.place
          const owner = review.owner
          const isRemoved = review.hidden_is_terminal === true

          if (!place || !owner) return null

          return (
            <article
              key={review.id}
              className="group/review flex items-start gap-3 sm:gap-4"
            >
              <PlaceHoverCard
                place={place}
                entitySheetTrackingContext="ProfileReview"
              >
                <Link
                  href={relativeCanonicalUrl(place.canonical_url)}
                  className="relative flex size-12 shrink-0 items-center justify-center rounded-xl bg-foreground/5"
                >
                  {place.photos?.length ? (
                    <Photo
                      src={place.photos[0]}
                      width="md"
                      alt={place.name}
                      className="absolute inset-0 size-full"
                    />
                  ) : (
                    <EntityIcon place={place} size={24} />
                  )}
                </Link>
              </PlaceHoverCard>
              <div className="flex-1">
                <div className="mt-0.5 leading-tight">
                  <PlaceHoverCard
                    place={place}
                    entitySheetTrackingContext="ProfileReview"
                  >
                    <Link
                      href={relativeCanonicalUrl(place.canonical_url)}
                      className="no-translate font-semibold"
                    >
                      {place.name}
                    </Link>
                  </PlaceHoverCard>
                  <span className="mx-0.5 text-muted"> &middot; </span>
                  <span className="mr-1.5 inline-block align-middle text-lg leading-tight">
                    {RATING_OPTIONS.find((o) => o.value === review.rating)
                      ?.emoji ?? ""}
                  </span>
                  <span className="text-sm text-muted">
                    {review.rating_label}
                  </span>
                </div>

                <PlaceLabel
                  place={place}
                  hasIcon={!!place.photos?.length}
                  className="text-sm leading-tight text-muted"
                />

                <ReviewHighlights review={review} className="mt-2.5" />

                {review.body || isRemoved ? (
                  <div className="no-translate mt-3 text-sm whitespace-pre-wrap">
                    {isRemoved ? REMOVED_REVIEW_MESSAGE : review.body}
                  </div>
                ) : null}

                {review.photo_urls?.length ? (
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {review.photo_urls.map((photo, index) => (
                      <AriaButton
                        key={photo.url}
                        as="div"
                        className="relative size-20 cursor-zoom-in rounded-xl"
                        onClick={() => {
                          openPhotosDialog({
                            photos: review.photo_urls ?? [],
                            activePhotoIndex: index,
                            title: place.name,
                            placeRef: place.entity_ref,
                          })
                        }}
                        aria-label={`View review photo ${index + 1}`}
                      >
                        <Photo
                          src={photo}
                          width="sm"
                          alt="Review"
                          className="absolute inset-0 size-full"
                        />
                      </AriaButton>
                    ))}
                  </div>
                ) : null}

                <p className="no-translate mt-2 text-2xs text-muted">
                  {formatElapsedTimeOrDate(review.updated_at)}
                </p>
              </div>
              {mode === "current" && (
                <ReviewActionsMenu
                  review={review}
                  triggerClassName="transition-opacity group-hover/review:opacity-100 can-hover:opacity-0"
                  onDeleted={(reviewId) => {
                    setReviews((prev) =>
                      prev.filter((item) => item.id !== reviewId)
                    )
                  }}
                />
              )}
            </article>
          )
        })}

        <div ref={loadMoreRef} />
        {isLoading && (
          <div className="flex h-10 items-center justify-center">
            <Loading />
          </div>
        )}
      </div>
      {PhotosDialog}
    </>
  )
}
